import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import InvoiceLinePricing from "./InvoiceLinePricing";
import { reviewPatch } from "../lib/invoiceReview";
import { api } from "../lib/api";

/**
 * InvoiceLineEditDialog — édition d'UNE ligne source de facture, réutilisée :
 *   1. Depuis « Voir la facture » (validée ou brouillon) — bouton Modifier
 *      sur chaque ligne du panneau Analyse.
 *   2. Depuis la fiche produit → clic sur un point du graphique évolution
 *      des prix (`ProductPriceChart` expose `invoice_id + line_id`).
 *
 * Contrat déterministe :
 *   - `invoice_id` + `line_id` sont la source stable de l'observation.
 *   - Save appelle `PATCH /api/invoices/{iid}/lines/{line_index}` (backend
 *     applique `apply_reviewed_measurement` → `compute_reference_price` →
 *     upsert `price_history` par `(product_id, invoice_id, line_id)` →
 *     recalcule `product.purchase_price` chronologiquement).
 *   - Aucun doublon, aucun nouvel OCR, aucun scan quota consommé.
 *   - `reviewed_base_unit_source="human"` marqué automatiquement par
 *     `reviewPatch` dès qu'un champ base/quantity/prix est édité.
 *   - Bouton Annuler ferme sans mutation, Enregistrer applique le patch.
 */
export default function InvoiceLineEditDialog({
  open, onOpenChange, invoiceId, lineIndex, initialLine, products = [],
  onSaved,
}) {
  const [line, setLine] = useState(initialLine || null);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => { setLine(initialLine || null); }, [initialLine, open]);
  useEffect(() => {
    if (!open) return;
    // Récupère les catégories canoniques déjà en usage (produits existants)
    // + set standard 9 catégories. Aucun appel réseau supplémentaire si
    // products est déjà chargé.
    const cats = new Set([
      "Vins", "Spiritueux", "Boissons", "Viandes", "Produits de la mer",
      "Crèmerie", "Fruits & Légumes", "Épicerie", "Consommables", "Divers",
    ]);
    (products || []).forEach((p) => p.category && cats.add(p.category));
    setCategories(Array.from(cats));
  }, [open, products]);

  const onChange = (patch) => setLine((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    if (!invoiceId || lineIndex == null || !line) return;
    setSaving(true);
    try {
      // On envoie UNIQUEMENT les champs édités (patch minimal). Le backend
      // applique apply_reviewed_measurement + recalcule catalogue+history.
      const patch = {
        product_name: line.product_name,
        matched_product_id: line.matched_product_id || null,
        category: line.category || null,
        category_source: line.category_source || undefined,
        reviewed_base_unit: line.reviewed_base_unit ?? null,
        reviewed_quantity: line.reviewed_quantity ?? null,
        reviewed_price_ht: line.reviewed_price_ht ?? null,
        reviewed_base_unit_source: line.reviewed_base_unit_source || undefined,
        line_total_ht: line.line_total_ht,
        content_value: line.content_value ?? null,
        content_unit: line.content_unit ?? null,
      };
      const res = await api.patchInvoiceLine(invoiceId, lineIndex, patch);
      toast.success("Ligne enregistrée", {
        description: "Prix produit, historique et recettes recalculés.",
      });
      onSaved?.(res);
      onOpenChange?.(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || "Enregistrement impossible.");
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  if (!open || !line) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="invoice-line-edit-dialog"
        className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-[15px]">
            Modifier la ligne source
          </DialogTitle>
          {line.raw_text && (
            <div className="text-[11px] font-mono text-[#5E6875] break-words">« {line.raw_text} »</div>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Nom produit + association */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs text-[#5E6875]">
              Nom produit
              <Input
                data-testid="edit-line-name"
                className="mt-1 !h-10"
                value={line.product_name || ""}
                onChange={(e) => onChange({ product_name: e.target.value })}
              />
            </label>
            <label className="text-xs text-[#5E6875]">
              Associer à
              <Select
                value={line.matched_product_id || "new"}
                onValueChange={(v) => onChange({
                  matched_product_id: v === "new" ? null : v,
                  ...(v !== "new" ? { status_tier: "confirmed", user_confirmed: true } : {}),
                })}
              >
                <SelectTrigger className="!h-10 text-[13px] mt-1" data-testid="edit-line-product-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">+ Nouveau produit</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="text-xs text-[#5E6875] sm:col-span-2">
              Catégorie (override manuel — persistant, non-écrasé par un futur scan)
              <Select
                value={line.category || "Divers"}
                onValueChange={(v) => onChange({ category: v, category_source: "human" })}
              >
                <SelectTrigger className="!h-10 text-[13px] mt-1" data-testid="edit-line-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
          </div>

          {/* Prix : réutilise InvoiceLinePricing (même formulaire que la vérification) */}
          <InvoiceLinePricing line={line} index={lineIndex ?? 0} onChange={onChange} />
        </div>

        <DialogFooter className="pt-3">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="edit-line-cancel">
            Annuler
          </Button>
          <Button onClick={save} disabled={saving} data-testid="edit-line-save" className="btn-orange border-0 !h-10 gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
